import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderColorProps,
  BoxShadowProps,
  DisplayProps,
  HeightProps,
  LeftProps,
  MarginLeftProps,
  PositionProps,
  RightProps,
  SizeProps,
  SpaceProps,
  TopProps,
  ZIndexProps,
} from 'styled-system';
import {
  backgroundColor,
  borderColor,
  boxShadow,
  display,
  height as heightUtil,
  left,
  marginLeft,
  position,
  right,
  size,
  space,
  themeGet,
  top,
  zIndex,
} from 'styled-system';

import type { StrokeProps } from '../../../theme/system-types';
import type { RotateProps, ThemeWidthProps, TransformProps } from '../../../theme/system-utilities';
import { rotate, stroke, themeWidth, transform } from '../../../theme/system-utilities';
import type { FlexProps } from '../../Flex';
import { Flex } from '../../Flex';
import Icon from '../../Icon';
import ControlButton from '../../image-carousel/components/ControlButton';

interface ControlsProps extends HeightProps {
  handleClick: () => void;
  isTabBar?: boolean;
  isPromotedFilters?: boolean;
  lightArrow?: boolean;
}

const ARROW_SIZE = 13;
const LIGHT_ARROW_SIZE = 24;

const ArrowIcon = styled(Icon)<SizeProps & StrokeProps & RotateProps & MarginLeftProps>`
  ${size};
  ${stroke};
  ${rotate};
  ${marginLeft};
`;

const arrowIconStyles: Partial<ComponentProps<typeof ArrowIcon>> = {
  stroke: 'sys.neutral.icon.default',
};

const Container = styled(Flex)<
  FlexProps &
    DisplayProps &
    PositionProps &
    TopProps &
    LeftProps &
    RightProps &
    TransformProps &
    HeightProps &
    ZIndexProps
>`
  ${display};
  ${position};
  ${top};
  ${left};
  ${right};
  ${transform};
  ${heightUtil};
  ${zIndex};
`;

const containerStyles: ComponentProps<typeof Container> = {
  alignItems: 'center',
  display: ['none', null, 'flex'],
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  height: '100%',
  zIndex: 2,
};

const StyledControlButton = styled(ControlButton)<BorderColorProps & BoxShadowProps & SpaceProps>`
  ${borderColor};
  ${boxShadow};
  ${space};

  &:hover {
    border-color: ${themeGet('colors.sys.neutral.border.hover')};
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
    svg {
      stroke: ${themeGet('colors.sys.neutral.icon.hover')};
    }
  }
`;

const getVariantStyles = (isTabBar?: boolean, isPromotedFilters?: boolean) => {
  if (isTabBar) {
    return { themeWidth: [44, null, null, 48], themeHeight: [44, null, null, 48] };
  }
  if (isPromotedFilters) {
    return { themeWidth: 32, themeHeight: 32 };
  }

  return { themeWidth: [40, null, null, 48], themeHeight: [40, null, null, 48] };
};

const getControlButtonStyles = (
  isTabBar?: boolean,
  isPromotedFilters?: boolean
): Partial<ComponentProps<typeof StyledControlButton>> => {
  const variantStyles = getVariantStyles(isTabBar, isPromotedFilters);

  return {
    display: 'flex',
    border: '1px solid',
    borderColor: 'sys.neutral.border.default',
    boxShadow: 'none',
    mt: '1px', // To align controls and chips vertically
    ...variantStyles,
  };
};

const OpacityGradient = styled.div<
  HeightProps & ThemeWidthProps & { toLeft?: boolean; isPromotedFilters?: boolean }
>`
  ${heightUtil};
  ${themeWidth};

  ${({ toLeft = false, isPromotedFilters = false }) => css`
    background: linear-gradient(
      to ${toLeft ? 'left' : 'right'},
      rgba(255, 255, 255, 0),
      ${isPromotedFilters ? 'rgba(238, 238, 239, 1)' : 'rgba(255, 255, 255, 1)'}
    );
  `};
`;

const opacityGradientStyles: ComponentProps<typeof OpacityGradient> = {
  themeWidth: ['sp_12', null, null, 'sp_16'],
};

const ButtonContainer = styled.div<HeightProps & BackgroundColorProps & SpaceProps>`
  ${heightUtil};
  ${backgroundColor};
  ${space};
`;

const getOpacityGradientHeight = (isTabBar, isPromotedFilters) => {
  if (isTabBar) return ['44px', null, null, '48px'];
  if (isPromotedFilters) return '32px';

  return '100%';
};

const NextControl = ({
  height,
  handleClick,
  isTabBar = false,
  isPromotedFilters = false,
  lightArrow,
}: ControlsProps) => {
  const isLightArrow = lightArrow ?? isPromotedFilters;

  return (
    <Container {...containerStyles} right="0">
      <OpacityGradient
        {...opacityGradientStyles}
        height={height ? height : getOpacityGradientHeight(isTabBar, isPromotedFilters)}
        isPromotedFilters={isPromotedFilters}
      />
      <ButtonContainer
        {...(isPromotedFilters
          ? { backgroundColor: 'sys.neutral.background.hover', pr: ['0', null, 'sp_48', 'sp_64'] }
          : { backgroundColor: 'sys.neutral.background.default' })}
      >
        <StyledControlButton
          {...getControlButtonStyles(isTabBar, isPromotedFilters)}
          {...(height && { themeWidth: height })}
          {...(height && { themeHeight: height })}
          onMouseDown={handleClick}
          data-testid="carousel-next-button"
          aria-hidden={true}
          tabIndex={-1}
          ml={['sp_12', null, null, 'sp_16']}
        >
          <ArrowIcon
            {...arrowIconStyles}
            name={isLightArrow ? 'arrowLightDown' : 'arrowDownStrong'}
            size={isLightArrow ? LIGHT_ARROW_SIZE : ARROW_SIZE}
            rotate="-90"
            marginLeft="sp_2"
          />
        </StyledControlButton>
      </ButtonContainer>
    </Container>
  );
};

const PrevControl = ({
  height,
  handleClick,
  isTabBar = false,
  isPromotedFilters = false,
  lightArrow,
}: ControlsProps) => {
  const isLightArrow = lightArrow ?? isPromotedFilters;

  return (
    <Container {...containerStyles} left="0">
      <ButtonContainer
        {...(isPromotedFilters
          ? { backgroundColor: 'sys.neutral.background.hover', pl: ['0', null, 'sp_48'] }
          : { backgroundColor: 'sys.neutral.background.default' })}
      >
        <StyledControlButton
          {...getControlButtonStyles(isTabBar, isPromotedFilters)}
          {...(height && { themeWidth: height })}
          {...(height && { themeHeight: height })}
          onMouseDown={handleClick}
          data-testid="carousel-prev-button"
          aria-hidden={true}
          tabIndex={-1}
          mr={['sp_12', null, null, 'sp_16']}
        >
          <ArrowIcon
            {...arrowIconStyles}
            name={isLightArrow ? 'arrowLightDown' : 'arrowDownStrong'}
            size={isLightArrow ? LIGHT_ARROW_SIZE : ARROW_SIZE}
            rotate="90"
            marginLeft="-2px"
          />
        </StyledControlButton>
      </ButtonContainer>
      <OpacityGradient
        {...opacityGradientStyles}
        height={height ? height : getOpacityGradientHeight(isTabBar, isPromotedFilters)}
        toLeft
        isPromotedFilters={isPromotedFilters}
      />
    </Container>
  );
};

export { PrevControl, NextControl };
